import { useRef, useState } from "react";
import { useDbClient } from "@tanstack/react-db";
import type { WikiViewProps } from "@softmax/web-ux/wiki";
import type { WikiRevisionPublic } from "@softmax/observatory-client/types";
import {
  titleFromSlug,
  wikiDraft,
  editConflict,
  type WikiEditConflict,
} from "./wiki-edit-state";
import { conflictView } from "../wiki-presentation";
import { saveWikiRevision } from "../wiki.mutations";
import type { SetMode, SetError } from "./wiki-editor.types";

import type { WikiPagePublic } from "@softmax/observatory-client/types";
import type { WikiIdentity } from "../wiki.collections";

type WikiEditorOptions = WikiIdentity & {
  readonly page: WikiPagePublic | null;
  readonly accessLost: boolean;
  readonly mode: WikiViewProps["mode"];
  readonly setMode: SetMode;
  readonly setError: SetError;
};

export function useWikiEditor({
  wikiSlug,
  pageSlug,
  elevatedPrivileges,
  page,
  accessLost,
  mode,
  setMode,
  setError,
}: WikiEditorOptions) {
  const db = useDbClient();
  const [storedDraft, setDraft] = useState(() => wikiDraft({ page, pageSlug }));
  const [storedBaseRevision, setBaseRevision] = useState<Pick<
    WikiRevisionPublic,
    "id" | "body"
  > | null>(page?.current_revision ?? null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<WikiEditConflict | null>(null);
  const editAction = useRef<{ fingerprint: string; key: string } | null>(null);
  const draft =
    mode === "read"
      ? {
          ...storedDraft,
          title: page?.title ?? titleFromSlug(pageSlug),
          body: page?.current_revision.body ?? "",
        }
      : storedDraft;
  const baseRevision =
    mode === "read" ? (page?.current_revision ?? null) : storedBaseRevision;
  const [lostAccess, setLostAccess] = useState(accessLost);

  if (accessLost !== lostAccess) {
    setLostAccess(accessLost);

    if (accessLost) {
      setDraft({ title: "", body: "", note: "" });
      setBaseRevision(null);
      setConflict(null);
      setMode("read");
    }
  }

  function changeMode(next: WikiViewProps["mode"]) {
    if (mode === "read" && next !== "read") {
      setDraft(draft);
      setBaseRevision(baseRevision);
    }

    setMode(next);
  }
  function save() {
    const fingerprint = `${draft.title}\n${draft.body}\n${draft.note}\n${baseRevision?.id ?? "create"}`;

    if (editAction.current?.fingerprint !== fingerprint)
      editAction.current = { fingerprint, key: crypto.randomUUID() };

    setSaving(true);
    setError(null);
    saveWikiRevision({
      db,
      wikiSlug,
      pageSlug,
      elevatedPrivileges,
      request: {
        title: draft.title,
        body: draft.body,
        note: draft.note || null,
        base_revision_id: baseRevision?.id ?? null,
        idempotency_key: editAction.current.key,
      },
    })
      .then(() => {
        editAction.current = null;
        setConflict(null);
        setMode("read");
      })
      .catch((reason: unknown) => {
        const detail = editConflict(reason);

        if (detail)
          setConflict({
            ...detail,
            baseBody: baseRevision?.body ?? "",
            proposedBody: draft.body,
          });
        else setError(reason instanceof Error ? reason.message : "Edit failed");
      })
      .finally(() => setSaving(false));
  }

  function cancelEdit() {
    setMode("read");
    setDraft(wikiDraft({ page, pageSlug }));
  }

  function merge() {
    if (!conflict) return;

    setDraft((previous) => ({ ...previous, body: conflict.proposedBody }));
    setBaseRevision({
      id: conflict.current_revision_id,
      body: conflict.current_body,
    });
    setConflict(null);
    setMode("edit");
  }

  return {
    editor: {
      draft: accessLost ? { title: "", body: "", note: "" } : draft,
      pending: saving,
      onDraftChange: setDraft,
      onSave: save,
      onCancel: cancelEdit,
      conflict: !accessLost && conflict ? conflictView(conflict) : null,
      onMerge: merge,
    },
    changeMode,
  };
}
